import Api from './api';

// =====================================================================
// Normalizers — API .NET retorna JSON tudo lowercase sem separadores
// (LowercaseContractResolver / LowercaseCaseNamingPolicy).
// Convertemos para camelCase pra usar idiomaticamente no front.
// USAR `??` (nullish coalescing) — não `||` — para preservar 0/false/''.
// =====================================================================

const normalizeStep = (s) => ({
    stepId: s.stepid ?? s.stepId ?? null,
    audience: s.audience ?? null,
    completed: s.completed ?? false,
    completedAt: s.completedat ?? s.completedAt ?? null,
});

const normalizeDemoSeedRefs = (r) => {
    if (!r || typeof r !== 'object') {
        return {
            chamadoNovoId: null,
            chamadoEmAndamentoId: null,
            chamadoEncerradoId: null,
            organizacaoId: null,
            timeId: null,
        };
    }
    return {
        chamadoNovoId: r.chamadonovoid ?? r.chamadoNovoId ?? null,
        chamadoEmAndamentoId: r.chamadoemandamentoid ?? r.chamadoEmAndamentoId ?? null,
        chamadoEncerradoId: r.chamadoencerradoid ?? r.chamadoEncerradoId ?? null,
        organizacaoId: r.organizacaoid ?? r.organizacaoId ?? null,
        timeId: r.timeid ?? r.timeId ?? null,
    };
};

const normalizeProgress = (p) => ({
    welcomeSeen: p.welcomeseen ?? p.welcomeSeen ?? null,
    audience: p.audience ?? '',
    totalSteps: p.totalsteps ?? p.totalSteps ?? 0,
    completedSteps: p.completedsteps ?? p.completedSteps ?? 0,
    progressPercent: p.progresspercent ?? p.progressPercent ?? 0,
    allCompleted: p.allcompleted ?? p.allCompleted ?? false,
    steps: Array.isArray(p.steps) ? p.steps.map(normalizeStep) : [],
    demoSeedRefs: normalizeDemoSeedRefs(p.demoseedrefs ?? p.demoSeedRefs ?? null),
});

const normalizeMarkStepResponse = (r) => ({
    stepId: r.stepid ?? r.stepId ?? null,
    completedAt: r.completedat ?? r.completedAt ?? null,
    alreadyCompleted: r.alreadycompleted ?? r.alreadyCompleted ?? false,
    allCompleted: r.allcompleted ?? r.allCompleted ?? false,
});

// =====================================================================

const OnboardingService = {
    /**
     * GET /api/onboarding/me
     * Retorna progresso do usuário logado: firstAccess + lista de steps.
     */
    async getMyProgress() {
        try {
            const result = await Api.get('/onboarding/me');
            if (result?.data?.success && result?.data?.data) {
                return normalizeProgress(result.data.data);
            }
            return null;
        } catch (err) {
            Api.kickoff(err);
            return null;
        }
    },

    /**
     * POST /api/onboarding/me/step/{stepId}/complete
     * Marca step manualmente. Usado p/ steps sem evento de domínio
     * (ex.: 'learn-automation-templates' ao terminar mini-tour).
     */
    async markStepComplete(stepId) {
        try {
            const result = await Api.post(`/onboarding/me/step/${encodeURIComponent(stepId)}/complete`);
            if (result?.data?.success && result?.data?.data) {
                return normalizeMarkStepResponse(result.data.data);
            }
            return null;
        } catch (err) {
            Api.kickoff(err);
            return null;
        }
    },

    /**
     * PATCH /api/onboarding/me/welcome-seen
     * Marca welcome_seen = true no tb_users (Welcome Modal foi visto).
     */
    async markWelcomeSeen() {
        try {
            const result = await Api.patch('/onboarding/me/welcome-seen');
            return result?.data?.success ?? false;
        } catch (err) {
            Api.kickoff(err);
            return false;
        }
    },

    /**
     * POST /api/onboarding/me/dismiss-all
     * Marca TODOS os steps validos do catalogo como completos pra esse usuario.
     * Usado pelo botao "Ja vi todos os passos" — admin experiente que nao
     * quer fazer o tour e quer remover o checklist da tela.
     */
    async dismissAll() {
        try {
            const result = await Api.post('/onboarding/me/dismiss-all');
            if (result?.data?.success && result?.data?.data) {
                const d = result.data.data;
                return {
                    dismissedAt: d.dismissedat ?? d.dismissedAt ?? null,
                    newlyMarked: d.newlymarked ?? d.newlyMarked ?? 0,
                    totalSteps: d.totalsteps ?? d.totalSteps ?? 0,
                };
            }
            return null;
        } catch (err) {
            Api.kickoff(err);
            return null;
        }
    },
};

export default OnboardingService;
